// composables/useCmsPage.js
import { watch, unref } from 'vue';
import { useGlobalStore } from '@/stores/global';
import { getCachedDataWithServerPrefetch } from '@/composables/useServerPrefetchCache';

export async function useCmsPage(apiSlugRef) {
  const globalStore = useGlobalStore();
  const route = useRoute();
  const lang = route.params.lang || 'uk';
  const asyncKey = computed(() => `cms-page-${lang}-${unref(apiSlugRef)}`);

  const { data, pending, error, refresh } = await useAsyncData(
    asyncKey.value,
    () => globalStore.fetchPageData(unref(apiSlugRef)),
    {
      server: true,
      getCachedData: getCachedDataWithServerPrefetch,
    },
  );

  // useAsyncData swallows fetch errors into `error` instead of rejecting, so a
  // 404 from the CMS must be thrown here (in the awaited setup flow) to reach
  // Nuxt's error page — throwing from a plain `watch` callback is silently
  // caught by Vue's error handler and never surfaces.
  if (error.value) {
    throw createError({
      statusCode: error.value.statusCode || 500,
      statusMessage:
        error.value.data?.error ||
        error.value.statusMessage ||
        'CMS Data Fetch Error',
      fatal: true,
    });
  }

  const handleRedirect = (pageData) => {
    if (pageData?.redirect?.redirect_url) {
      const { redirect_url, redirect_status } = pageData.redirect;
      const currentPath = route.fullPath;
      const isSamePage =
        redirect_url === currentPath ||
        redirect_url === `${useRequestURL().origin}${currentPath}`;

      if (!isSamePage) {
        return navigateTo(redirect_url, {
          redirectCode: parseInt(redirect_status) || 301,
          external: redirect_url.startsWith('http'),
        });
      } else {
        console.warn('Recursive redirect detected and blocked:', redirect_url);
      }
    }
  };

  handleRedirect(data.value);

  watch(data, (newData) => {
    if (newData) handleRedirect(newData);
  });

  watch(error, (newError) => {
    if (newError) {
      throw createError({
        statusCode: newError.statusCode || 500,
        statusMessage:
          newError.data?.error ||
          newError.statusMessage ||
          'CMS Data Fetch Error',
        fatal: true,
      });
    }
  });

  // Feeds server/plugins/lastModified.js: only set when the CMS gave us a
  // real content date, so pages without one fall back to server boot time
  // instead of a fabricated one.
  if (process.server) {
    const modified = data.value?.data?.modified;
    const parsedModified = modified ? new Date(modified) : null;
    if (parsedModified && !Number.isNaN(parsedModified.getTime())) {
      const event = useRequestEvent();
      if (event) event.context.pageLastModified = parsedModified;
    }
  }

  return { data, pending, error, refresh };
}
